import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GroupReschedule } from "@/components/GroupReschedule";

export default function GroupBookingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  // Iter125 — When coach approves a booking that used a date range, they
  // must pick the concrete slot. Preload with the mid-point of the range
  // to save the coach a tap.
  const [chosenStart, setChosenStart] = useState("");
  // Iter132 — Coach's per-booking toggle for credit payment. Defaults off;
  // hidden entirely for club bookings.
  const [allowCredit, setAllowCredit] = useState(false);
  // Iter163 — Editable total price on the approval card. If the request came
  // in with a stale £0 (coach group_session_rate wasn't set at request time,
  // or first-priority coach didn't have one) the coach previously had no way
  // to fix it. Pre-fill with the current price_total; coach can override
  // before hitting Approve.
  const [priceInput, setPriceInput] = useState("");
  // Iter164 — Note attached to a post-approval price edit (why the change?).
  const [priceNote, setPriceNote] = useState("");
  // Iter139 — Live credit balance shown on the pay panel so the player knows
  // how many credits they have before hitting "Use 1 Credit". Only fetched
  // when the coach has enabled credit payments AND the player still owes a
  // share — otherwise the badge stays hidden.
  const [credits, setCredits] = useState(null);

  const load = () => {
    api.get(`/group-bookings/${id}`).then((r) => setData(r.data));
    api.get(`/group-bookings/${id}/status`).then((r) => setStatus(r.data)).catch(() => {});
  };
  useEffect(load, [id]);

  // Iter120 — Tick every 60s so the countdown pill refreshes without a full
  // status reload.
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  // Iter125 — Prefill the chosen-slot picker with the range's start day at
  // a sensible default hour (18:00 if evenings requested, else 12:00).
  useEffect(() => {
    if (!data || chosenStart) return;
    if (data.date_range_start && data.date_range_end) {
      const day = data.date_range_start.slice(0, 10);
      const win = (data.preferred_time_window || "").split("-")[0] || "18:00";
      const hh = /^\d{2}:\d{2}$/.test(win) ? win : "18:00";
      setChosenStart(`${day}T${hh}`);
    } else if (data.start) {
      setChosenStart(data.start.slice(0, 16));
    }
  }, [data, chosenStart]);

  // Iter163 — Prefill the price field with whatever total came in on the
  // request. Empty string when data isn't loaded so the input is controlled.
  useEffect(() => {
    if (!data) return;
    if (priceInput === "") {
      setPriceInput(String(Number(data.price_total || 0)));
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="p-8 text-[#A3A3A3]">Loading…</div>;

  const isCoach = user?.id === data.coach_id;
  const isAdmin = user?.role === "admin" || (user?.permissions || []).some((p) => p === "admin" || p === "super_admin");
  const isOrganiser = user?.id === data.organiser_id;
  const myPayment = status?.payments?.find((p) => p.player_id === user?.id);

  // Iter120 — Countdown to payment_deadline_at (only when active).
  let countdown = null;
  if (data.payment_deadline_at && data.status === "approved_awaiting_payment") {
    const ms = new Date(data.payment_deadline_at).getTime() - nowTick;
    if (ms > 0) {
      const hours = Math.floor(ms / 3600000);
      const mins = Math.floor((ms % 3600000) / 60000);
      const urgent = hours < 6;
      countdown = { hours, mins, urgent };
    } else {
      countdown = { hours: 0, mins: 0, urgent: true, expired: true };
    }
  }

  const act = async (path, body, msg) => {
    setBusy(true);
    try { await api.post(`/group-bookings/${id}/${path}`, body); toast.success(msg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const pay = async (method) => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/group-bookings/${id}/pay`, {}, { params: { method } });
      if (r.url) window.location.href = r.url;
      else { toast.success(`Paid with ${method}`); load(); }
    } catch (e) { toast.error(e.response?.data?.detail || "Payment failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 py-6" data-testid="group-booking-detail">
      <h1 className="h-display text-4xl">{data.sport} · {data.venue_name}</h1>
      <div className="card-surface p-6 space-y-2">
        <div className="text-sm"><span className="text-[#A3A3A3]">Status:</span> <span className="text-[#CCFF00]" data-testid="gb-status">{data.status}</span></div>
        <div className="text-sm"><span className="text-[#A3A3A3]">Coach:</span> {data.coach_name}</div>
        {data.date_range_start && data.date_range_end ? (
          <div className="text-sm" data-testid="gb-range-display">
            <span className="text-[#A3A3A3]">Proposed window:</span>{" "}
            {data.date_range_start} → {data.date_range_end}
            {data.preferred_time_window && <span className="text-[#CCFF00] ml-2">({data.preferred_time_window})</span>}
          </div>
        ) : (
          <div className="text-sm"><span className="text-[#A3A3A3]">Start:</span> {data.start}</div>
        )}
        {data.date_range_start && data.start && data.status !== "pending_coach" && (
          <div className="text-sm"><span className="text-[#A3A3A3]">Confirmed slot:</span> <span className="text-[#CCFF00]">{data.start}</span></div>
        )}
        {/* Iter133 — Post-approval chip so the coach can see the credit
            toggle they flipped on stayed sticky when they revisit. Solo
            & Group only; Club never allows credits so no chip. */}
        {data.status !== "pending_coach" && data.allow_credit_payment && (
          <div
            data-testid="gb-credits-allowed-chip"
            className="inline-flex items-center gap-1.5 px-2 py-1 border border-[#CCFF00] bg-[#CCFF0010] text-[10px] uppercase tracking-wider text-[#CCFF00] font-bold mt-1"
          >
            ✦ Credits allowed for this session
          </div>
        )}
        <div className="text-sm"><span className="text-[#A3A3A3]">Duration:</span> {data.duration_min} min</div>
        <div className="text-sm"><span className="text-[#A3A3A3]">Focus:</span> {(data.focus_areas || []).join(", ")}</div>
        <div className="text-sm"><span className="text-[#A3A3A3]">Total:</span> £{data.price_total}</div>
        {data.payment_deadline_at && <div className="text-sm"><span className="text-[#A3A3A3]">Payment deadline:</span> {data.payment_deadline_at}</div>}
        {countdown && (
          <div
            data-testid="gb-countdown"
            className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-wider font-bold ${countdown.urgent ? "bg-[#FF4500] text-black" : "bg-[#FF9F1C] text-black"}`}
          >
            {countdown.expired
              ? "Deadline passed"
              : `${countdown.hours}h ${countdown.mins}m left to pay`}
          </div>
        )}
      </div>

      {isCoach && data.status === "pending_coach" && (
        <div className="card-surface p-6 space-y-3">
          <div className="label-mono">COACH ACTIONS</div>
          {data.date_range_start && data.date_range_end && (
            <div className="space-y-2" data-testid="gb-slot-picker">
              <div className="text-xs uppercase tracking-wider text-[#A3A3A3]">
                Pick the exact slot ({data.date_range_start} → {data.date_range_end}
                {data.preferred_time_window ? ` · ${data.preferred_time_window}` : ""})
              </div>
              <input
                type="datetime-local"
                value={chosenStart}
                min={`${data.date_range_start.slice(0, 10)}T00:00`}
                max={`${data.date_range_end.slice(0, 10)}T23:59`}
                onChange={(e) => setChosenStart(e.target.value)}
                data-testid="gb-chosen-start"
                className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-white"
              />
            </div>
          )}
          {/* Iter163 — Editable total price. Shown per-player breakdown
              underneath so the coach can see what each invitee will pay
              (split_pay divides by max_players). */}
          <div className="space-y-1" data-testid="gb-price-editor">
            <label className="text-xs uppercase tracking-wider text-[#A3A3A3]" htmlFor="gb-price-input">
              Total price (£)
            </label>
            <div className="flex items-center gap-2">
              <span className="text-lg text-[#A3A3A3]">£</span>
              <input
                id="gb-price-input"
                type="number"
                min="0"
                step="0.01"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                data-testid="gb-price-input"
                className="w-32 bg-[#141414] border border-[#262626] px-3 py-2 text-white text-lg focus:outline-none focus:border-[#CCFF00]"
              />
              {Number(data.max_players || 0) > 1 && Number(priceInput || 0) > 0 && (
                <span className="text-xs text-[#A3A3A3]" data-testid="gb-price-per-player-hint">
                  ≈ £{(Number(priceInput || 0) / Number(data.max_players)).toFixed(2)} per player
                </span>
              )}
            </div>
            {Number(data.price_total || 0) === 0 && (
              <div className="text-[10px] text-[#FF9F1C] uppercase tracking-wider mt-0.5">
                Came in at £0 — your group rate wasn't set when this was requested. Type a price now.
              </div>
            )}
          </div>
          {/* Iter132 — Coach toggles whether players may redeem a credit
              (from a performance package) instead of paying by card.
              Hidden for Club (backend forces false anyway). */}
          {!(Number(data.max_players || 0) >= 7 || data.cascade_broadcast) && (
            <label className="flex items-start gap-2 text-xs text-[#A3A3A3] cursor-pointer" data-testid="gb-credit-toggle">
              <input type="checkbox" checked={allowCredit} onChange={(e) => setAllowCredit(e.target.checked)}
                className="mt-0.5 accent-[#CCFF00]" data-testid="gb-credit-toggle-input" />
              <span>
                <span className="text-white font-semibold">Allow players to pay with credits</span>
                <span className="block text-[10px] text-[#525252] mt-0.5">
                  Players on a performance package can redeem a credit instead of paying by card. Others still pay by card as usual.
                </span>
              </span>
            </label>
          )}
          <button type="button" onClick={() => {
              if (data.date_range_start && data.date_range_end && !chosenStart) {
                toast.error("Pick a start time inside the proposed window");
                return;
              }
              const priceNum = Number(priceInput);
              if (!Number.isFinite(priceNum) || priceNum < 0) {
                toast.error("Enter a valid total price");
                return;
              }
              // Iter163 — send the override only when it actually differs
              // from the incoming total so we don't accidentally re-write £0.
              const currentTotal = Number(data.price_total || 0);
              const override = priceNum !== currentTotal ? priceNum : null;
              act("approve", {
                payment_deadline_hours: 24,
                chosen_start: chosenStart || null,
                allow_credit_payment: allowCredit,
                ...(override != null ? { price_total_override: override } : {}),
              }, "Approved");
            }}
            disabled={busy} data-testid="gb-approve"
            className="px-5 py-2 bg-[#CCFF00] text-black text-xs uppercase tracking-wider font-bold disabled:opacity-50">
            Approve · 24h payment window
          </button>
          <button type="button" onClick={() => act("decline", { reason: window.prompt("Reason?") || "" }, "Declined")}
            disabled={busy} data-testid="gb-decline"
            className="px-5 py-2 border border-[#FF4500] text-[#FF4500] text-xs uppercase tracking-wider">
            Decline
          </button>
        </div>
      )}

      {(isCoach || isAdmin) && ["approved_awaiting_payment", "confirmed"].includes(data.status) && (
        <GroupReschedule bookingId={id} onDone={load} />
      )}

      {(isCoach || isAdmin) && data.status === "approved_awaiting_payment" && (
        <div className="card-surface p-6 space-y-3" data-testid="gb-adjust-price">
          <div className="label-mono">ADJUST TOTAL PRICE</div>
          <p className="text-xs text-[#A3A3A3]">
            Repairs a stale total (e.g. a £0 that came in before your group rate was set). Every <strong>pending</strong> share recomputes to <span className="text-white">total ÷ number of pending payers</span>. If anyone has already paid, cancel + rebook instead.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-lg text-[#A3A3A3]">£</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={priceInput}
              onChange={(e) => setPriceInput(e.target.value)}
              data-testid="gb-adjust-price-input"
              className="w-32 bg-[#141414] border border-[#262626] px-3 py-2 text-white text-lg focus:outline-none focus:border-[#CCFF00]"
            />
            <input
              type="text"
              value={priceNote}
              onChange={(e) => setPriceNote(e.target.value)}
              placeholder="Optional note (why the change?)"
              data-testid="gb-adjust-price-note"
              className="flex-1 min-w-[220px] bg-[#141414] border border-[#262626] px-3 py-2 text-white text-sm focus:outline-none focus:border-[#CCFF00]"
            />
            <button
              type="button"
              onClick={async () => {
                const priceNum = Number(priceInput);
                if (!Number.isFinite(priceNum) || priceNum < 0) {
                  toast.error("Enter a valid price");
                  return;
                }
                if (priceNum === Number(data.price_total || 0)) {
                  toast.info("Price unchanged");
                  return;
                }
                setBusy(true);
                try {
                  const res = await api.patch(`/group-bookings/${id}/price`, {
                    price_total: priceNum, note: priceNote,
                  });
                  toast.success(`Total set to £${priceNum.toFixed(2)} · ${res.data?.pending_shares_updated || 0} share(s) updated`);
                  setPriceNote("");
                  load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Couldn't update the price");
                } finally { setBusy(false); }
              }}
              disabled={busy}
              data-testid="gb-adjust-price-save"
              className="px-4 py-2 bg-[#CCFF00] text-black text-xs uppercase tracking-wider font-bold disabled:opacity-50"
            >
              Save new price
            </button>
          </div>
          {data.price_edited_at && (
            <div className="text-[10px] text-[#525252] uppercase tracking-wider" data-testid="gb-price-edited-chip">
              Last edited {new Date(data.price_edited_at).toLocaleString()}
              {data.price_edit_note ? ` · ${data.price_edit_note}` : ""}
            </div>
          )}
        </div>
      )}

      {status?.payments?.length > 0 && (
        <div className="card-surface p-6 space-y-3" data-testid="gb-payment-status-card">
          <div className="label-mono">PAYMENT STATUS ({status.paid_count}/{status.payments.length} paid)</div>
          {status.payments.map((p) => {
            const canReconcile = (isCoach || isAdmin) && p.status !== "paid";
            return (
              <div key={p.id} className="border-b border-[#262626] py-2 space-y-2" data-testid={`gb-pay-row-${p.player_id}`}>
                <div className="flex items-center justify-between text-sm">
                  <span>{p.player_name}</span>
                  <span className={p.status === "paid" ? "text-[#CCFF00]" : p.status === "missed" ? "text-[#FF4500]" : p.status === "waived" ? "text-[#66C2FF]" : "text-[#A3A3A3]"}>
                    {p.status} · £{p.share_amount}
                  </span>
                </div>
                {canReconcile && Number(p.credits_remaining || 0) > 0 && (
                  <div className="text-[10px] text-[#FF9F1C] uppercase tracking-wider" data-testid={`gb-credits-hint-${p.player_id}`}>
                    {p.credits_remaining} session credit{p.credits_remaining === 1 ? "" : "s"} left · use one below
                  </div>
                )}
                {canReconcile && Number(p.credits_remaining || 0) === 0 && (
                  <div className="text-[10px] text-[#525252] uppercase tracking-wider" data-testid={`gb-credits-hint-${p.player_id}`}>
                    0 credits · "Use credit" won't work until they buy a package
                  </div>
                )}
                {canReconcile && (
                  <div className="flex flex-wrap gap-2" data-testid={`gb-reconcile-${p.player_id}`}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={async () => {
                        setBusy(true);
                        try {
                          await api.post(`/group-bookings/${id}/reconcile-share`, {
                            player_id: p.player_id, action: "mark_paid_offline",
                            method_label: "cash",
                          });
                          toast.success(`${p.player_name}: paid offline — now enrolled`);
                          load();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Couldn't reconcile");
                        } finally { setBusy(false); }
                      }}
                      data-testid={`gb-mark-paid-offline-${p.player_id}`}
                      className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black disabled:opacity-40"
                    >
                      Paid at court
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={async () => {
                        const note = prompt("Reason for waiving fee? (optional)", "") || "";
                        setBusy(true);
                        try {
                          await api.post(`/group-bookings/${id}/reconcile-share`, {
                            player_id: p.player_id, action: "waive", note,
                          });
                          toast.success(`${p.player_name}: fee waived — now enrolled`);
                          load();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Couldn't reconcile");
                        } finally { setBusy(false); }
                      }}
                      data-testid={`gb-waive-${p.player_id}`}
                      className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black disabled:opacity-40"
                    >
                      Waive
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={async () => {
                        setBusy(true);
                        try {
                          await api.post(`/group-bookings/${id}/reconcile-share`, {
                            player_id: p.player_id, action: "assign_credit",
                          });
                          toast.success(`${p.player_name}: paid from credit — now enrolled`);
                          load();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Couldn't reconcile");
                        } finally { setBusy(false); }
                      }}
                      data-testid={`gb-assign-credit-${p.player_id}`}
                      className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#FF9F1C] text-[#FF9F1C] hover:bg-[#FF9F1C] hover:text-black disabled:opacity-40"
                    >
                      Use credit
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={async () => {
                        if (!confirm(`Remove ${p.player_name} from this booking?`)) return;
                        setBusy(true);
                        try {
                          await api.post(`/group-bookings/${id}/reconcile-share`, {
                            player_id: p.player_id, action: "remove",
                          });
                          toast.success(`${p.player_name} removed`);
                          load();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Couldn't remove");
                        } finally { setBusy(false); }
                      }}
                      data-testid={`gb-remove-${p.player_id}`}
                      className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#404040] text-[#A3A3A3] hover:border-[#FF4500] hover:text-[#FF4500] disabled:opacity-40"
                    >
                      Remove
                    </button>
                  </div>
                )}
                {p.reconciled_at && (
                  <div className="text-[10px] text-[#525252] uppercase tracking-wider" data-testid={`gb-reconciled-chip-${p.player_id}`}>
                    Reconciled {new Date(p.reconciled_at).toLocaleString()}
                    {p.method ? ` · ${p.method}` : ""}
                    {p.reconcile_note ? ` · ${p.reconcile_note}` : ""}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {myPayment && myPayment.status === "pending" && data.status === "approved_awaiting_payment" && (
        <PayShare
          bookingId={id}
          data={data}
          myPayment={myPayment}
          busy={busy}
          onPay={pay}
          credits={credits}
          setCredits={setCredits}
        />
      )}

      {isOrganiser && ["pending_coach", "approved_awaiting_payment"].includes(data.status) && (
        <button type="button" onClick={() => act("cancel", {}, "Cancelled")} disabled={busy}
          data-testid="gb-cancel"
          className="px-5 py-2 border border-[#FF4500] text-[#FF4500] text-xs uppercase tracking-wider">
          Cancel booking
        </button>
      )}

      {/* Iter140 — Refund timeline card. Shown once the booking is cancelled
          so the organiser sees the money story at a glance: credits
          restored, cash refunded, admin fee, and the 5-10 day Stripe SLA. */}
      {isOrganiser && data.status === "cancelled" && data.refund_report && (
        <RefundTimeline
          report={data.refund_report}
          cancelledAt={data.cancelled_at}
          feePct={data.admin_fee_pct ?? 10}
        />
      )}
    </div>
  );
}

/* -------------------- Refund timeline card (iter140) -------------------- */
function RefundTimeline({ report, cancelledAt, feePct }) {
  const creditRestored = Number(report?.credit_restored || 0);
  const cashRefund = Number(report?.cash_refunded_pence || 0) / 100;
  const adminFee = Number(report?.admin_fee_pence || 0) / 100;
  const cancelledDate = cancelledAt
    ? new Date(cancelledAt).toLocaleDateString(undefined, {
        weekday: "short", day: "numeric", month: "short", year: "numeric",
      })
    : "—";
  const nothingRefunded = creditRestored === 0 && cashRefund === 0;
  return (
    <div className="card-surface p-6 space-y-3" data-testid="gb-refund-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="label-mono text-[#CCFF00]">REFUND SUMMARY</div>
        <span
          data-testid="gb-refund-cancelled-at"
          className="text-[10px] uppercase tracking-wider text-[#A3A3A3]"
        >
          Cancelled {cancelledDate}
        </span>
      </div>
      {nothingRefunded && (
        <div className="text-sm text-[#A3A3A3]" data-testid="gb-refund-nothing">
          No payments were settled — nothing to refund.
        </div>
      )}
      {creditRestored > 0 && (
        <div className="text-sm flex items-center gap-2" data-testid="gb-refund-credit-line">
          <span className="text-[#CCFF00]">●</span>
          <span>
            <strong>{creditRestored}</strong> session credit{creditRestored === 1 ? "" : "s"} restored to your account
          </span>
        </div>
      )}
      {cashRefund > 0 && (
        <div className="space-y-1" data-testid="gb-refund-cash-line">
          <div className="text-sm flex items-center gap-2">
            <span className="text-[#00BFFF]">●</span>
            <span>
              <strong>£{cashRefund.toFixed(2)}</strong> refunded to your card
              {adminFee > 0 && (
                <span className="text-[#A3A3A3]"> (after £{adminFee.toFixed(2)} admin fee · {feePct}%)</span>
              )}
            </span>
          </div>
          <div className="text-[11px] text-[#A3A3A3] pl-4" data-testid="gb-refund-sla">
            Card refunds typically land in 5-10 working days.
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------- Pay share panel (iter139) -------------------- */
function PayShare({ bookingId, data, myPayment, busy, onPay, credits, setCredits }) {
  const creditsAllowed = !!data.allow_credit_payment;
  const balance = credits?.total_remaining ?? null;
  useEffect(() => {
    // Only fetch when credits are actually accepted for this booking — no
    // point pinging the credits API when the coach turned the toggle off.
    if (!creditsAllowed) return;
    api.get("/me/credits").then((r) => setCredits(r.data)).catch(() => setCredits({ total_remaining: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, creditsAllowed]);

  const zeroBalance = creditsAllowed && balance === 0;
  const creditBtnDisabled = busy || zeroBalance || balance === null;

  // Iter141 — Expiry countdown chip. Only shown when the coach accepted
  // credits AND the player has a live balance AND the next-expiry date is
  // within 30 days. Colour ramps up as the deadline nears (orange ≤7d,
  // red ≤3d) so nobody quietly loses a pack.
  const expiryChip = (() => {
    if (!creditsAllowed || !balance || balance <= 0) return null;
    const iso = credits?.next_expiry;
    if (!iso) return null;
    const msLeft = new Date(iso).getTime() - Date.now();
    const days = Math.ceil(msLeft / 86400000);
    if (days > 30 || days < 0) return null;
    let label, tone;
    if (days === 0) { label = "Credits expire today"; tone = "red"; }
    else if (days === 1) { label = "Credits expire tomorrow"; tone = "red"; }
    else if (days <= 3) { label = `Credits expire in ${days} days`; tone = "red"; }
    else if (days <= 7) { label = `Credits expire in ${days} days`; tone = "amber"; }
    else { label = `Credits expire in ${days} days`; tone = "muted"; }
    const style = tone === "red"
      ? { borderColor: "#FF4500", color: "#FF4500", background: "#FF450010" }
      : tone === "amber"
        ? { borderColor: "#FF9F1C", color: "#FF9F1C", background: "#FF9F1C10" }
        : { borderColor: "#262626", color: "#A3A3A3", background: "transparent" };
    return { label, style, days };
  })();

  return (
    <div className="card-surface p-6" data-testid="gb-pay-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="label-mono">PAY YOUR SHARE · £{myPayment.share_amount}</div>
        {creditsAllowed && (
          <span
            data-testid="gb-credit-balance-badge"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold border ${
              zeroBalance
                ? "border-[#FF4500] text-[#FF4500] bg-[#FF450010]"
                : "border-[#CCFF00] text-[#CCFF00] bg-[#CCFF0010]"
            }`}
          >
            {balance === null
              ? "Checking credits…"
              : `${balance} credit${balance === 1 ? "" : "s"} available`}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {creditsAllowed && (
          <button
            onClick={() => onPay("credit")}
            disabled={creditBtnDisabled}
            data-testid="gb-pay-credit"
            className="px-4 py-2 bg-[#CCFF00] text-black text-xs uppercase tracking-wider font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            title={zeroBalance ? "You have no credits — pay with card" : undefined}
          >
            Use 1 Credit
          </button>
        )}
        <button
          onClick={() => onPay("stripe")}
          disabled={busy}
          data-testid="gb-pay-stripe"
          className="px-4 py-2 border border-[#262626] text-white text-xs uppercase tracking-wider disabled:opacity-40"
        >
          Pay with card
        </button>
      </div>
      {zeroBalance && (
        <Link
          to="/offerings"
          data-testid="gb-credit-zero-note"
          className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#FF4500] hover:text-[#CCFF00] underline underline-offset-2 transition-colors"
        >
          You have no session credits left — tap to buy a pack, or pay by card →
        </Link>
      )}
      {expiryChip && (
        <div
          data-testid="gb-credit-expiry-chip"
          className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold border"
          style={expiryChip.style}
          title="Use your credits before the pack expires."
        >
          ⏳ {expiryChip.label}
        </div>
      )}
      {!creditsAllowed && (
        <div
          data-testid="gb-credit-not-allowed-note"
          className="mt-2 text-[11px] text-[#525252]"
        >
          Credits are not accepted for this booking. Please pay by card.
        </div>
      )}
    </div>
  );
}
